'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState, useTransition } from 'react';

import { useIsMobile } from './useMobile';

type UseUrlViewModeArgs<T extends string> = {
  /** The set of accepted modes. Anything else falls back to `defaultMode`. */
  validModes: ReadonlySet<T>;
  /** Mode used when the param is absent or invalid; also the param-cleared value. */
  defaultMode: T;
  /**
   * Optional mobile-only default. When the viewport is mobile AND the user
   * hasn't picked a view via the URL param, this mode wins over `defaultMode`.
   * Cards/kanban don't fit small screens — list is usually the best fallback.
   */
  mobileDefaultMode?: T;
  /** Search-param key. Defaults to `view`. */
  paramKey?: string;
};

/**
 * View-mode state synced to a URL search param, with instant optimistic
 * updates.
 *
 * Reading the mode straight from `searchParams` makes the grid/list/kanban
 * toggle lag behind the click — `router.replace` is an async App Router
 * navigation, so the view only flips once that round-trip settles. This hook
 * holds local state for an instant render and persists the URL inside a
 * non-blocking `startTransition`, while still honouring browser back/forward
 * and deep links via the effect sync.
 *
 * Returns a `[viewMode, setViewMode]` tuple, same shape as `useState`.
 */
export function useUrlViewMode<T extends string>({
  validModes,
  defaultMode,
  mobileDefaultMode,
  paramKey = 'view',
}: UseUrlViewModeArgs<T>) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [, startTransition] = useTransition();
  const isMobile = useIsMobile();

  // Mobile viewports prefer a different default (e.g. list over cards/kanban),
  // but only when the user hasn't pinned a view via the URL.
  const effectiveDefault
    = mobileDefaultMode !== undefined && isMobile === true
      ? mobileDefaultMode
      : defaultMode;

  const parse = useCallback(
    (raw: string | null, fallback: T): T => {
      if (raw !== null && validModes.has(raw as T) === true) {
        return raw as T;
      }
      return fallback;
    },
    [validModes],
  );

  const urlMode = parse(searchParams.get(paramKey), effectiveDefault);
  const [viewMode, setLocalViewMode] = useState<T>(urlMode);

  // Honour external URL changes — browser back/forward, deep links, a space
  // switch that resets the query — without fighting the optimistic update.
  // Also picks up the mobile-default swap once `useIsMobile` settles on the
  // client (initial render is desktop-default to keep SSR markup stable).
  useEffect(() => {
    setLocalViewMode(urlMode);
  }, [urlMode]);

  const setViewMode = useCallback(
    (next: T) => {
      // Flip the view immediately; the URL catches up in the background.
      setLocalViewMode(next);
      startTransition(() => {
        const params = new URLSearchParams(searchParams.toString());
        // Clear the param only when an ABSENT param would resolve to exactly the
        // selected mode (i.e. it equals the effective default for this viewport).
        // On mobile the effective default is `mobileDefaultMode`, so deleting the
        // param for a `defaultMode` pick would let the absent-param resolve back
        // to the mobile default and the sync effect would revert the selection.
        // Writing it explicitly in that case keeps the URL and the pick in sync.
        if (next === effectiveDefault) {
          params.delete(paramKey);
        } else {
          params.set(paramKey, next);
        }
        const queryString = params.toString();
        router.replace(
          `${pathname}${queryString.length > 0 ? `?${queryString}` : ''}`,
          { scroll: false },
        );
      });
    },
    [effectiveDefault, paramKey, pathname, router, searchParams],
  );

  return [viewMode, setViewMode] as const;
}
